/**
 * MonthRangePicker — "Mês inicial → mês final" com presets rápidos.
 * value = { start: "AAAA-MM", end: "AAAA-MM" }
 */
import { CalendarRange } from "lucide-react";
import { MONTHS_PT } from "@/lib/officeReport";

const now = new Date();
const CUR_Y = now.getFullYear();
const CUR_M = now.getMonth() + 1;
const key = (y, m) => `${y}-${String(m).padStart(2, "0")}`;
const split = (k) => k.split("-").map(Number);
const shift = (y, m, delta) => {
  const idx = y * 12 + (m - 1) + delta;
  return key(Math.floor(idx / 12), (idx % 12) + 1);
};

export const PRESETS = [
  { id: "year", label: "Este ano", range: () => ({ start: key(CUR_Y, 1), end: key(CUR_Y, 12) }) },
  { id: "last12", label: "Últimos 12 meses", range: () => ({ start: shift(CUR_Y, CUR_M, -11), end: key(CUR_Y, CUR_M) }) },
  { id: "prev-year", label: "Ano anterior", range: () => ({ start: key(CUR_Y - 1, 1), end: key(CUR_Y - 1, 12) }) },
  { id: "quarter", label: "Últimos 3 meses", range: () => ({ start: shift(CUR_Y, CUR_M, -2), end: key(CUR_Y, CUR_M) }) },
];

function MonthSelect({ value, onChange, years, testid }) {
  const [y, m] = split(value);
  return (
    <div className="inline-flex items-center gap-1" data-testid={testid}>
      <select
        value={m}
        onChange={(e) => onChange(key(y, Number(e.target.value)))}
        className="text-xs bg-background border border-border rounded-full pl-3 pr-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-foreground/20"
        data-testid={`${testid}-month`}
      >
        {MONTHS_PT.map((lbl, i) => <option key={lbl} value={i + 1}>{lbl}</option>)}
      </select>
      <select
        value={y}
        onChange={(e) => onChange(key(Number(e.target.value), m))}
        className="text-xs bg-background border border-border rounded-full pl-3 pr-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-foreground/20"
        data-testid={`${testid}-year`}
      >
        {years.map((yy) => <option key={yy} value={yy}>{yy}</option>)}
      </select>
    </div>
  );
}

export default function MonthRangePicker({ value, onChange, years }) {
  const allYears = Array.from(new Set([...years, ...split(value.start).slice(0, 1), ...split(value.end).slice(0, 1), CUR_Y])).sort((a, b) => b - a);
  const invalid = value.start > value.end;
  const activePreset = PRESETS.find((p) => { const r = p.range(); return r.start === value.start && r.end === value.end; })?.id;
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="office-report-period">
      <CalendarRange className="w-4 h-4 text-muted-foreground" strokeWidth={1.6} />
      <MonthSelect value={value.start} onChange={(s) => onChange({ ...value, start: s })} years={allYears} testid="office-report-period-start" />
      <span className="text-xs text-muted-foreground">→</span>
      <MonthSelect value={value.end} onChange={(e) => onChange({ ...value, end: e })} years={allYears} testid="office-report-period-end" />
      <div className="flex items-center gap-1 flex-wrap" data-testid="office-report-period-presets">
        {PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onChange(p.range())}
            data-testid={`office-report-preset-${p.id}`}
            className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
              activePreset === p.id
                ? "bg-foreground text-background border-foreground"
                : "text-muted-foreground border-border hover:text-foreground hover:border-foreground/40"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>
      {invalid && (
        <span className="text-[11px] text-destructive" data-testid="office-report-period-error">O mês inicial tem de ser anterior ao final</span>
      )}
    </div>
  );
}
